// As chamadas de Anúncios ao servidor. Chaves de consulta: ['anuncios', loja, ...].
// Dinheiro sempre em centavos, como o servidor manda; a tela formata.
// Nenhuma regra da Meta mora aqui: o servidor confere e devolve a mensagem.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api/api';
import { useLoja } from '../../../shared/estado/loja';
import type {
  AlertasDosAnuncios, AnuncioOk, CampanhaDeAnuncioCriada, CidadesDaMeta, ConfirmacaoDaAtivacao, DetalheDaCampanhaDeAnuncio,
  EstadoDoGoogleAds, LocaisDoGoogle, OpcoesDoGoogleAds, SincronizacaoDoGoogle, SugestaoParaGoogle,
  EstadoDosAnuncios, InteressesDaMeta, OpcoesDaContaDeAnuncio, PainelDosAnuncios, PecaEnviada, PecaNaCampanha, PosicionamentoMudado, PreviaDoPosicionamento, SincronizacaoDosAnuncios,
  SugestaoDeAnuncio, UmAtivo, ValidacaoDaPeca,
} from '../../../shared/api/tipos-gerados';

export type Campanha = PainelDosAnuncios['campanhas'][number];
export type Numeros = PainelDosAnuncios['total'];
export type Alerta = AlertasDosAnuncios['alertas'][number];
export type Objetivo = 'mensagens' | 'vendas';
export type Destino = 'whatsapp' | 'direct' | 'site';

export interface Periodo { desde: string; ate: string }

/** Onde o anúncio da Meta aparece; a leitura pode voltar `personalizado` (editado no Gerenciador). */
export type Posicionamentos = PosicionamentoMudado['para'];
export type PosicionamentosLidos = NonNullable<Campanha['posicionamentos']>;

/** As escolhas do seletor "Onde aparece", na ordem da tela. */
export const POSICIONAMENTOS: Array<{ valor: Posicionamentos; rotulo: string }> = [
  { valor: 'automatico', rotulo: 'Automático (a Meta escolhe)' },
  { valor: 'instagram', rotulo: 'Só no Instagram' },
  { valor: 'facebook', rotulo: 'Só no Facebook' },
  { valor: 'instagram_e_facebook', rotulo: 'Instagram e Facebook' },
];
export const EXPLICACAO_DOS_POSICIONAMENTOS =
  'Automático deixa a Meta escolher (inclui Facebook, Messenger e Audience Network). Só no Instagram mostra no feed, stories, reels e explorar.';
export const ROTULO_DO_POSICIONAMENTO: Record<PosicionamentosLidos, string> = {
  automatico: 'Automático', instagram: 'Só no Instagram', facebook: 'Só no Facebook', instagram_e_facebook: 'Instagram e Facebook',
  personalizado: 'Personalizado (editado no Gerenciador)',
};

export interface NovaCampanha {
  chave: string; nome: string; objetivo: Objetivo; destino: Destino;
  publico: {
    cidades: Array<{ chave: string; nome: string; raioKm: number }>; idadeMin: number; idadeMax: number;
    genero: 'todos' | 'mulheres' | 'homens'; interesses: Array<{ id: string; nome: string }>;
  };
  pecas: string[]; texto: string; titulo: string; boasVindas?: string | null;
  orcamentoDiario: number; inicio?: string | null; fim?: string | null;
  posicionamentos?: Posicionamentos;
}

const lojaAtual = () => encodeURIComponent(useLoja.getState().tenantId ?? '');
const base = () => `/api/tenant/${lojaAtual()}/anuncios`;
const useLojaAtual = () => useLoja((s) => s.tenantId) ?? '';
const consulta = (p?: Periodo) => (p ? `?desde=${p.desde}&ate=${p.ate}` : '');

export function useEstadoDosAnuncios(ligado = true) {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['anuncios', loja, 'estado'], queryFn: () => api.get<EstadoDosAnuncios>(base()), enabled: loja !== '' && ligado, staleTime: 60_000 });
}

export type Rede = 'meta' | 'google';

/** A Performance Max do assistente do Google (dinheiro em centavos). */
export interface NovaPmax {
  chave: string; nome: string; titulos: string[]; titulosLongos: string[]; descricoes: string[]; nomeDaEmpresa: string;
  imagemPaisagem: string; imagemQuadrada: string; logo: string; locais: Array<{ chave: string; nome: string }>;
  orcamentoDiario: number; inicio?: string | null; fim?: string | null;
}

export function useEstadoDoGoogle(ligado = true) {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['anuncios', loja, 'google'], queryFn: () => api.get<EstadoDoGoogleAds>(`${base()}/google`), enabled: loja !== '' && ligado, staleTime: 60_000 });
}

export function useOpcoesDoGoogle(ligado: boolean) {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['anuncios', loja, 'google', 'opcoes'], queryFn: () => api.get<OpcoesDoGoogleAds>(`${base()}/google/opcoes`), enabled: loja !== '' && ligado });
}

export const urlDoLoginGoogle = () => api.get<{ ok: true; url: string }>(`/api/tenant/${lojaAtual()}/google-ads/oauth-url`).then((r) => r.url);
export const escolherContaGoogle = (contaId: string) => api.put<AnuncioOk>(`${base()}/google/conta`, { contaId });
export const desconectarGoogle = () => api.delete<AnuncioOk>(`${base()}/google/conexao`);
export const sincronizarGoogle = () => api.post<SincronizacaoDoGoogle>(`${base()}/google/sincronizar`, {});
export const criarPmax = (d: NovaPmax) => api.post<CampanhaDeAnuncioCriada>(`${base()}/google/campanhas`, d);
export const sugerirGoogle = (ideia: string | null) => api.post<SugestaoParaGoogle>(`${base()}/google/sugestoes`, { ideia });
export const buscarLocais = (q: string) => api.get<LocaisDoGoogle>(`${base()}/google/busca/locais?q=${encodeURIComponent(q)}`).then((r) => r.locais);

export function useOpcoes(contaId: string | null, ligado: boolean) {
  const loja = useLojaAtual();
  return useQuery({
    queryKey: ['anuncios', loja, 'opcoes', contaId],
    queryFn: () => api.get<OpcoesDaContaDeAnuncio>(`${base()}/opcoes${contaId ? `?conta=${encodeURIComponent(contaId)}` : ''}`),
    enabled: loja !== '' && ligado,
  });
}

export function usePainel(p: Periodo, ligado: boolean) {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['anuncios', loja, 'painel', p.desde, p.ate], queryFn: () => api.get<PainelDosAnuncios>(`${base()}/painel${consulta(p)}`), enabled: loja !== '' && ligado });
}

/** Enquanto a campanha está sendo criada, a tela se atualiza sozinha. */
export function useCampanha(id: string, p: Periodo) {
  const loja = useLojaAtual();
  return useQuery({
    queryKey: ['anuncios', loja, 'campanha', id, p.desde, p.ate],
    queryFn: () => api.get<DetalheDaCampanhaDeAnuncio>(`${base()}/campanhas/${encodeURIComponent(id)}${consulta(p)}`),
    enabled: loja !== '' && id !== '',
    refetchInterval: (q) => (q.state.data?.campanha.status === 'criando' ? 5_000 : false),
  });
}

export function useAlertas(ligado: boolean) {
  const loja = useLojaAtual();
  return useQuery({ queryKey: ['anuncios', loja, 'alertas'], queryFn: () => api.get<AlertasDosAnuncios>(`${base()}/alertas`).then((r) => r.alertas), enabled: loja !== '' && ligado });
}

export function useRecarregarAnuncios() {
  const consultas = useQueryClient();
  const loja = useLojaAtual();
  return (...parte: string[]) => consultas.invalidateQueries({ queryKey: ['anuncios', loja, ...parte] });
}

export const urlDoLogin = () => api.get<{ ok: true; url: string }>(`/api/tenant/${lojaAtual()}/meta-ads/oauth-url`).then((r) => r.url);
export const escolherConta = (d: { contaId: string; paginaId: string; pixelId?: string | null; whatsappNumero?: string | null }) => api.put<AnuncioOk>(`${base()}/conta`, d);
export const desconectar = () => api.delete<AnuncioOk>(`${base()}/conexao`);
export const sincronizar = () => api.post<SincronizacaoDosAnuncios>(`${base()}/sincronizar`, {});
export const criarCampanha = (d: NovaCampanha) => api.post<CampanhaDeAnuncioCriada>(`${base()}/campanhas`, d);
export const retomar = (id: string) => api.post<AnuncioOk>(`${base()}/campanhas/${id}/retomar`, {});
export const descartar = (id: string) => api.delete<AnuncioOk>(`${base()}/campanhas/${id}`);
export const confirmacao = (id: string) => api.get<ConfirmacaoDaAtivacao>(`${base()}/campanhas/${id}/confirmacao`);
export const ativar = (id: string, c: { orcamentoDiario: number | null; total: number | null }) => api.post<AnuncioOk>(`${base()}/campanhas/${id}/ativar`, c);
export const pausar = (id: string) => api.post<AnuncioOk>(`${base()}/campanhas/${id}/pausar`, {});
export const mudarOrcamento = (id: string, orcamentoDiario: number) => api.put<AnuncioOk>(`${base()}/campanhas/${id}/orcamento`, { orcamentoDiario });
export const previaDoPosicionamento = (id: string, posicionamentos: Posicionamentos) =>
  api.get<PreviaDoPosicionamento>(`${base()}/campanhas/${encodeURIComponent(id)}/posicionamentos?posicionamentos=${posicionamentos}`);
export const mudarPosicionamento = (id: string, posicionamentos: Posicionamentos) =>
  api.put<PosicionamentoMudado>(`${base()}/campanhas/${encodeURIComponent(id)}/posicionamentos`, { posicionamentos });
export const duplicar = (id: string) => api.post<CampanhaDeAnuncioCriada>(`${base()}/campanhas/${id}/duplicar`, {});
export const adicionarPeca = (campanhaId: string, ativoId: string) => api.post<PecaNaCampanha>(`${base()}/campanhas/${encodeURIComponent(campanhaId)}/pecas`, { ativoId });
export const trocarCriativo = (anuncioId: string, ativoId: string) => api.put<AnuncioOk>(`${base()}/anuncios/${anuncioId}/criativo`, { ativoId });
export const validarPeca = (ativoId: string) => api.get<ValidacaoDaPeca>(`${base()}/pecas/validar?ativoId=${encodeURIComponent(ativoId)}`);
export const enviarPeca = (ativoId: string) => api.post<PecaEnviada>(`${base()}/pecas`, { ativoId });
export const recortarPeca = (ativoId: string, proporcao: string) => api.post<UmAtivo>(`${base()}/pecas/recortar`, { ativoId, proporcao }).then((r) => r.ativo);
export const sugerir = (d: { objetivo: Objetivo; destino: Destino; ideia?: string | null }) => api.post<SugestaoDeAnuncio>(`${base()}/sugestoes`, d);
export const buscarInteresses = (q: string) => api.get<InteressesDaMeta>(`${base()}/busca/interesses?q=${encodeURIComponent(q)}`).then((r) => r.interesses);
export const buscarCidades = (q: string) => api.get<CidadesDaMeta>(`${base()}/busca/cidades?q=${encodeURIComponent(q)}`).then((r) => r.cidades);
export const marcarVisto = (id: string) => api.post<AnuncioOk>(`${base()}/alertas/${id}/visto`, {});

/** R$ a partir de centavos. */
export const reais = (centavos: number | null | undefined) =>
  centavos == null ? '—' : (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Os últimos `dias` dias até hoje, no fuso de quem usa (o servidor fecha o dia em Brasília). */
export function ultimosDias(dias: number): Periodo {
  const d = (n: number) => { const x = new Date(Date.now() - n * 864e5); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 10); };
  return { desde: d(dias - 1), ate: d(0) };
}
